import { VisuallyHidden } from '@coop/components';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { useA11yLogInConsoleEffect } from '../hooks/useA11yLogInConsole';
import { useAppDispatch, useAppSelector } from '../hooks/useThunkDispatch';
import { currentPageSelectors } from '../selectors/currentPageSelectors';
import { currentPageActions } from '../store/slices/currentPage/currentPageSlice';

const createContainerNode = (type: string) => {
    const existingContainer = document.getElementsByTagName(type)[0];
    if (existingContainer) {
        return existingContainer;
    }
    const container = document.createElement(type);
    container.style.cssText = 'position:absolute; bottom: 0;';
    container.setAttribute('inert-ignore', 'true');
    document.body.appendChild(container);
    return container;
};

const createAnnouncerNode = (container: Element) => {
    const announcer = document.createElement('div');
    announcer.ariaLive = 'assertive';
    announcer.role = 'alert';

    container.appendChild(announcer);
    return announcer;
};

const useAnnouncerContainerNode = (announcerType: string) => {
    const [containerNode, setContainerNode] = useState<Element | null>(null);

    useEffect(() => {
        const container = createContainerNode(announcerType);
        setContainerNode(container);
        return () => {
            const existingContainer = document.getElementsByTagName(announcerType)[0];
            if (existingContainer?.isConnected) {
                document.body.removeChild(container);
            }
        };
    }, [announcerType]);

    return containerNode;
};

const useAnnouncerPortalNode = (announcerType: string) => {
    const container = useAnnouncerContainerNode(announcerType);

    const announcerNode = useMemo(() => {
        if (!container?.firstElementChild && container) {
            const announcer = createAnnouncerNode(container);
            container?.appendChild(announcer);
        }
        return container?.firstElementChild;
    }, [container]);

    return announcerNode;
};

const useCurrentPageTitleAnnouncer = () => {
    const accessibleTitle = useAppSelector(currentPageSelectors.selectA11yPageTitle);

    const [announcedTitle, setAnnouncedTitle] = useState(accessibleTitle);
    const previousAnnouncedTitle = useRef<string | undefined>(undefined);

    useEffect(() => {
        if (
            !!accessibleTitle &&
            // First page load is handled by screen reader out of the box, it's only the subsequent page changes that need to be announced
            previousAnnouncedTitle.current !== undefined &&
            previousAnnouncedTitle.current !== accessibleTitle
        ) {
            setAnnouncedTitle(accessibleTitle);
        }
        previousAnnouncedTitle.current = accessibleTitle;
    }, [accessibleTitle, setAnnouncedTitle]);

    return announcedTitle;
};

export const CurrentPageTitleAnnouncer = () => {
    const portalNode = useAnnouncerPortalNode('route-announcer');
    const announcedTitle = useCurrentPageTitleAnnouncer();

    useA11yLogInConsoleEffect('CurrentPageTitleAnnouncer', announcedTitle);

    return portalNode
        ? createPortal(<VisuallyHidden>{announcedTitle}</VisuallyHidden>, portalNode)
        : null;
};

export const CurrentPageAnnouncer = () => {
    const portalNode = useAnnouncerContainerNode('page-announcer');
    const allyAnnouncement = useAppSelector(currentPageSelectors.selectA11yAnnouncement);
    const dispatch = useAppDispatch();

    const timer = useRef<NodeJS.Timeout | null>(null);
    const ALLY_TIMER_INTERVAL = 1000;

    useEffect(() => {
        if (allyAnnouncement) {
            if (timer.current) clearTimeout(timer.current);

            timer.current = setTimeout(() => {
                dispatch(currentPageActions.setA11yAnnouncement(undefined));
            }, ALLY_TIMER_INTERVAL);
        }

        return () => {
            if (timer.current) clearTimeout(timer.current);
        };
    }, [allyAnnouncement, dispatch]);

    useA11yLogInConsoleEffect(
        'CurrentPageAnnouncer',
        allyAnnouncement?.sender,
        allyAnnouncement?.text,
    );

    return portalNode
        ? createPortal(
              <VisuallyHidden
                  aria-live={allyAnnouncement?.ariaLive || 'polite'}
                  role="status"
                  aria-atomic="true"
              >
                  {allyAnnouncement?.text}
              </VisuallyHidden>,
              portalNode,
          )
        : null;
};
